import {
  CreateLeadRequest,
  Lead,
  LeadGender,
  LeadStatus,
  UpdateLeadRequest,
} from '../../types/lead';
import { DEFAULT_PHONE_COUNTRY_CODE, parsePhoneNumber } from '../users/profileFormConstants';
import { validateContactPhoneNumber } from '../users/profileFormValidation';
import { validateEmail } from '../../utils/validation';

export interface LeadFormData {
  fullName: string;
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneE164: string;
  leadSourceId: string;
  status: LeadStatus;
  gender: string;
  preferredGender: string;
  notes: string;
  leadDate: string;
  counsellorName: string;
  age: string;
  qualification: string;
  occupation: string;
  caste: string;
  height: string;
  maritalStatus: string;
  divorceInfo: string;
  location: string;
  requirements: string;
  remarks1stTime: string;
  secondCallDate: string;
  thirdCallDate: string;
  biodataReceived: string;
  profileCreated: string;
  membership: string;
  amountProfilesShared: string;
  shared: string;
  left: string;
  retainerFormSent: string;
  retainerSigned: string;
  anyOtherRemark: string;
}

export const INITIAL_LEAD_FORM: LeadFormData = {
  fullName: '',
  email: '',
  phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
  phoneNumber: '',
  phoneE164: '',
  leadSourceId: '',
  status: 'new_lead',
  gender: '',
  preferredGender: '',
  notes: '',
  leadDate: '',
  counsellorName: '',
  age: '',
  qualification: '',
  occupation: '',
  caste: '',
  height: '',
  maritalStatus: '',
  divorceInfo: '',
  location: '',
  requirements: '',
  remarks1stTime: '',
  secondCallDate: '',
  thirdCallDate: '',
  biodataReceived: '',
  profileCreated: '',
  membership: '',
  amountProfilesShared: '',
  shared: '',
  left: '',
  retainerFormSent: '',
  retainerSigned: '',
  anyOtherRemark: '',
};

const toFormString = (value: string | null | undefined): string => value?.trim() ?? '';

export const getFullPhoneFromForm = (form: LeadFormData): string =>
  form.phoneE164.trim();

export const mapLeadToFormData = (lead: Lead): LeadFormData => {
  const normalizedPhone = lead.phoneNumber?.replace(/\s+/g, '') ?? '';
  const { countryCode, localNumber } = parsePhoneNumber(normalizedPhone);

  return {
    fullName: lead.fullName,
    email: lead.email,
    phoneCountryCode: countryCode,
    phoneNumber: localNumber,
    phoneE164: normalizedPhone,
    leadSourceId: lead.leadSource ? String(lead.leadSource.id) : '',
    status: lead.status,
    gender: toFormString(lead.gender),
    preferredGender: toFormString(lead.preferredGender),
    notes: toFormString(lead.notes),
    leadDate: toFormString(lead.leadDate),
    counsellorName: toFormString(lead.counsellorName),
    age: toFormString(lead.age),
    qualification: toFormString(lead.qualification),
    occupation: toFormString(lead.occupation),
    caste: toFormString(lead.caste),
    height: toFormString(lead.height),
    maritalStatus: toFormString(lead.maritalStatus),
    divorceInfo: toFormString(lead.divorceInfo),
    location: toFormString(lead.location),
    requirements: toFormString(lead.requirements),
    remarks1stTime: toFormString(lead.remarks1stTime),
    secondCallDate: toFormString(lead.secondCallDate),
    thirdCallDate: toFormString(lead.thirdCallDate),
    biodataReceived: toFormString(lead.biodataReceived),
    profileCreated: toFormString(lead.profileCreated),
    membership: toFormString(lead.membership),
    amountProfilesShared: toFormString(lead.amountProfilesShared),
    shared: toFormString(lead.shared),
    left: toFormString(lead.left),
    retainerFormSent: toFormString(lead.retainerFormSent),
    retainerSigned: toFormString(lead.retainerSigned),
    anyOtherRemark: toFormString(lead.anyOtherRemark),
  };
};

const optionalString = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const optionalGender = (value: string): LeadGender | undefined => {
  if (value === 'Male' || value === 'Female') return value;
  return undefined;
};

const buildCommonPayloadFields = (
  form: LeadFormData,
  fullPhone: string
): Omit<CreateLeadRequest, 'fullName' | 'email' | 'leadSourceId'> => ({
  phoneNumber: fullPhone || undefined,
  status:
    form.status === 'converted'
      ? undefined
      : (form.status as Exclude<LeadStatus, 'converted'>),
  gender: optionalGender(form.gender),
  preferredGender: optionalGender(form.preferredGender),
  notes: optionalString(form.notes),
  leadDate: optionalString(form.leadDate),
  counsellorName: optionalString(form.counsellorName),
  age: optionalString(form.age),
  qualification: optionalString(form.qualification),
  occupation: optionalString(form.occupation),
  caste: optionalString(form.caste),
  height: optionalString(form.height),
  maritalStatus: optionalString(form.maritalStatus),
  divorceInfo: optionalString(form.divorceInfo),
  location: optionalString(form.location),
  requirements: optionalString(form.requirements),
  remarks1stTime: optionalString(form.remarks1stTime),
  secondCallDate: optionalString(form.secondCallDate),
  thirdCallDate: optionalString(form.thirdCallDate),
  biodataReceived: optionalString(form.biodataReceived),
  profileCreated: optionalString(form.profileCreated),
  membership: optionalString(form.membership),
  amountProfilesShared: optionalString(form.amountProfilesShared),
  shared: optionalString(form.shared),
  left: optionalString(form.left),
  retainerFormSent: optionalString(form.retainerFormSent),
  retainerSigned: optionalString(form.retainerSigned),
  anyOtherRemark: optionalString(form.anyOtherRemark),
});

export const mapFormToCreatePayload = (
  form: LeadFormData,
  trimmedEmail: string
): CreateLeadRequest => ({
  fullName: form.fullName.trim(),
  email: trimmedEmail,
  leadSourceId: Number(form.leadSourceId),
  ...buildCommonPayloadFields(form, getFullPhoneFromForm(form)),
});

const payloadFromForm = (
  form: LeadFormData,
  trimmedEmail: string
): UpdateLeadRequest => ({
  fullName: form.fullName.trim(),
  email: trimmedEmail,
  leadSourceId: form.leadSourceId ? Number(form.leadSourceId) : undefined,
  ...buildCommonPayloadFields(form, getFullPhoneFromForm(form)),
});

const payloadFromInitialForm = (
  form: LeadFormData,
  trimmedEmail: string
): UpdateLeadRequest => payloadFromForm(form, trimmedEmail);

const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === undefined && b === '') return true;
  if (a === '' && b === undefined) return true;
  return false;
};

export const mapFormToUpdatePayload = (
  form: LeadFormData,
  initialForm: LeadFormData,
  trimmedEmail: string
): UpdateLeadRequest => {
  const current = payloadFromForm(form, trimmedEmail);
  const initial = payloadFromInitialForm(initialForm, initialForm.email.trim());
  const diff: UpdateLeadRequest = {};

  (Object.keys(current) as (keyof UpdateLeadRequest)[]).forEach((key) => {
    const nextValue = current[key];
    const prevValue = initial[key];
    if (!valuesEqual(nextValue, prevValue)) {
      (diff as Record<string, unknown>)[key] = nextValue;
    }
  });

  return diff;
};

export const isLeadFormValid = (
  form: LeadFormData,
  fullPhone: string
): boolean => {
  if (!form.fullName.trim() || !form.leadSourceId) return false;
  if (!validateEmail(form.email).valid) return false;
  if (fullPhone && validateContactPhoneNumber(fullPhone)) return false;
  return true;
};

export const getPhoneValidationError = (
  fullPhone: string,
  phoneTouched: boolean
): string | null => {
  if (!phoneTouched || !fullPhone) return null;
  return validateContactPhoneNumber(fullPhone);
};
